import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  TableSortLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { Task } from './types';

interface TasksListViewProps {
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const statusLabel: Record<string, string> = {
  todo: 'Att göra',
  doing: 'Pågår',
  done: 'Klart',
};

const statusColor = (status: string): 'default' | 'warning' | 'success' => {
  if (status === 'doing') return 'warning';
  if (status === 'done') return 'success';
  return 'default';
};

const categoryLabel: Record<string, string> = {
  Content: 'Innehåll',
  Testing: 'Testning',
  Process: 'Process',
  Release: 'Release',
};

const statusOrder: Record<string, number> = {
  todo: 1,
  doing: 2,
  done: 3,
};

const priorityColor = (priority: number): 'error' | 'warning' | 'info' | 'default' => {
  if (priority === 1) return 'error';
  if (priority === 2) return 'warning';
  if (priority === 3) return 'info';
  return 'default';
};

const priorityLabel = (priority: number): string => {
  if (priority === 1) return 'Kritisk';
  if (priority === 2) return 'Hög';
  if (priority === 3) return 'Normal';
  return 'Låg';
};

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('sv-SE');
  } catch {
    return value;
  }
};

const TasksListView: React.FC<TasksListViewProps> = ({ tasks, onEditTask, onDeleteTask, canEdit = true, canDelete = true }) => {
  const [sortKey, setSortKey] = useState<'priority' | 'title' | 'status' | 'category' | 'assigneeName' | 'updatedAt'>('priority');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedTasks = useMemo(() => {
    const data = [...tasks];
    data.sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'priority':
          diff = a.priority - b.priority;
          break;
        case 'title':
          diff = a.title.localeCompare(b.title);
          break;
        case 'status':
          diff = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
          break;
        case 'category':
          diff = (a.category || '').localeCompare(b.category || '');
          break;
        case 'assigneeName':
          diff = (a.assigneeName || '').localeCompare(b.assigneeName || '');
          break;
        case 'updatedAt':
          diff = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        default:
          diff = 0;
      }
      return sortDir === 'asc' ? diff : -diff;
    });
    return data;
  }, [tasks, sortKey, sortDir]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (!tasks.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Inga uppgifter ännu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Klicka på "Ny uppgift" ovan för att skapa din första uppgift.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell sortDirection={sortKey === 'priority' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'priority'} direction={sortKey === 'priority' ? sortDir : 'asc'} onClick={() => handleSort('priority')}>
                Prioritet
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'title' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'title'} direction={sortKey === 'title' ? sortDir : 'asc'} onClick={() => handleSort('title')}>
                Uppgift
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'status' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'status'} direction={sortKey === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'category' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'category'} direction={sortKey === 'category' ? sortDir : 'asc'} onClick={() => handleSort('category')}>
                Kategori
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'assigneeName' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'assigneeName'} direction={sortKey === 'assigneeName' ? sortDir : 'asc'} onClick={() => handleSort('assigneeName')}>
                Tilldelad
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'updatedAt' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'updatedAt'} direction={sortKey === 'updatedAt' ? sortDir : 'asc'} onClick={() => handleSort('updatedAt')}>
                Senast ändrad
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Åtgärder</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTasks.map((task) => (
            <TableRow key={task._id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
              <TableCell>
                <Tooltip title={priorityLabel(task.priority)}>
                  <Chip 
                    size="small" 
                    label={`P${task.priority}`} 
                    color={priorityColor(task.priority)}
                    sx={{ minWidth: 40 }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell sx={{ maxWidth: 400 }}>
                <Typography variant="body2" fontWeight={600}>
                  {task.title}
                </Typography>
                {task.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {task.description.length > 100 ? task.description.substring(0, 100) + '...' : task.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  size="small" 
                  label={statusLabel[task.status] || task.status} 
                  color={statusColor(task.status)}
                  variant={task.status === 'done' ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>
                <Chip size="small" label={categoryLabel[task.category] || task.category} variant="outlined" />
              </TableCell>
              <TableCell>
                {task.assigneeName ? (
                  <Chip 
                    size="small" 
                    icon={<PersonIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label={task.assigneeName} 
                    color="primary"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(task.updatedAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  {canEdit && (
                    <Tooltip title="Redigera">
                      <IconButton size="small" onClick={() => onEditTask(task)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Ta bort">
                      <IconButton size="small" color="error" onClick={() => onDeleteTask(task._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TasksListView;
